import React from 'react';
import { Formatter, Utils } from 'ecrion-components';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import { useUser } from '../context/UserContext';

const getTagValue = (tags, name) => {
  const titleTag = tags.find((tag) => tag.Name === name);
  return titleTag ? titleTag.Value : undefined;
};

const getLinkLocation = (pathname, search = '', hash = '') => {
  return {
    pathname: pathname,
    search: search,
    hash: hash,
    state: {
      from: {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
      },
    },
  };
};

const removeUnnecessaryExtension = (name) => {
  if (name.endsWith('.pdf') || name.endsWith('.docx')) return name;
  const extensionIndex = name.lastIndexOf('.');
  if (extensionIndex !== -1) return name.substring(0, extensionIndex);
  return name;
};
const titleColumn = {
  id: 'Name',
  children: 'Name',
  serverSort: true,
  formatter: ({ data }) => {
    const title = removeUnnecessaryExtension(
      getTagValue(data.Tags, 'Title') || 'Unnamed'
    );
    const encodedEnvironment = encodeURIComponent(useUser().Environment);

    const params = {
      jobId: getTagValue(data.Tags, 'JobId'),
      communicationId: getTagValue(data.Tags, 'CommunicationId'),
    };

    const location = getLinkLocation(
      `/${encodedEnvironment}/File`,
      `?${Utils.url.stringifySearchParams(params)}`
    );
    return <Formatter.Link text={title} to={location} component={RouterLink} />;
  },
  width: {
    xs: '30%',
  },
};

const descriptionColumn = {
  id: 'Description',
  children: 'Description',
  formatter: ({ data }) => getTagValue(data.Tags, 'Description') || null,
  width: {
    xs: '50%',
  },
};

const lastModifiedColumn = {
  id: 'LastModifiedUTC',
  children: 'Last Updated',
  serverSort: true,
  formatter: ({ data }) =>
    moment(data.LastModifiedUTC).format('MM/DD/YYYY, h:mm:ss A'),
  width: {
    xs: '20%',
  },
};

const FORMS = {
  data: {
    url: `${process.env.REACT_APP_WEBSITE_URL}/backend/portalfiles?category=forms&filter=communications`,
    itemUrl: '',
    dataKey: 'forms',
    idKeyProp: 'FilePath',
  },
  columns: [titleColumn, descriptionColumn, lastModifiedColumn],
};

const JOBS = {
  data: {
    url: `${process.env.REACT_APP_WEBSITE_URL}/backend/portalfiles?category=forms&filter=jobs`,
    itemUrl: '',
    dataKey: 'jobs',
    idKeyProp: 'FilePath',
  },
  columns: [
    titleColumn,
    {
      id: 'Status',
      children: 'Status',
      formatter: ({ data }) => (
        <Formatter.JobStatus
          value={
            data.Status
          }
          label={data.Status.match(/[A-Z][a-z]+/g).join(" ")}
        />
      ),
      width: {
        xs: '50%',
      },
    },
    lastModifiedColumn,
  ],
};

const ANALYTICS = {
  data: {
    url: `${process.env.REACT_APP_WEBSITE_URL}/backend/portalfiles?category=analytics&filter=`,
    itemUrl: '',
    dataKey: 'analytics',
    idKeyProp: 'FilePath',
  },
  columns: [
    {
      id: 'Name',
      children: 'Name',
      serverSort: true,
      formatter: ({ data }) => {
        const encodedEnvironment = encodeURIComponent(useUser().Environment);

        const params = {
          title: getTagValue(data.Tags, 'Title') || data.Name,
          file: data.Name,
        };
        const linkText = removeUnnecessaryExtension(params.title);

        const location = getLinkLocation(
          `/${encodedEnvironment}/File`,
          `?${Utils.url.stringifySearchParams(params)}`,
          `#workspace/${data.WorkspaceName}/${data.FilePath}`
        );

        return (
          <Formatter.Link
            text={linkText}
            to={location}
            component={RouterLink}
          />
        );
      },
      width: {
        xs: '30%',
      },
    },
    descriptionColumn,
    lastModifiedColumn,
  ],
};

const DOCUMENTS = {
  data: {
    url: `${process.env.REACT_APP_WEBSITE_URL}/backend/portalfiles?category=documents&filter=`,
    itemUrl: '',
    dataKey: 'documents',
    idKeyProp: 'FilePath',
  },
  columns: [
    {
      id: 'Name',
      children: 'Name',
      serverSort: true,
      formatter: ({ data }) => {
        const params = {
          title: getTagValue(data.Tags, 'Title'),
          file: data.Name,
        };
        const communicationId = getTagValue(data.Tags, 'CommunicationId');
        const encodedEnvironment = encodeURIComponent(useUser().Environment);
        const linkText = removeUnnecessaryExtension(params.title || data.Name);

        let location = {};
        if (communicationId)
          location = getLinkLocation(
            `/${encodedEnvironment}/OnDemand`,
            `?${Utils.url.stringifySearchParams({
              communication: params.title,
              ContactId: useUser().Contact.SynchronizationId,
            })}`
          );
        else
          location = getLinkLocation(
            `/${encodedEnvironment}/File`,
            `?${Utils.url.stringifySearchParams(params)}`,
            `#workspace/${data.WorkspaceName}/${data.FilePath}`
          );

        return (
          <>
            {communicationId ? (
              <Formatter.Link
                text={linkText}
                to={location}
                component={RouterLink}
              />
            ) : (
              <Formatter.Link
                text={linkText}
                to={location}
                component={RouterLink}
              />
            )}
          </>
        );
      },
      width: {
        xs: '30%',
      },
    },
    descriptionColumn,
    lastModifiedColumn,
  ],
};

const CUSTOM = {
  data: {
    url: `${process.env.REACT_APP_WEBSITE_URL}/backend/portalfiles?category={category}&filter={filter}`,
    itemUrl: '',
    dataKey: 'customDocuments',
    idKeyProp: 'FilePath',
    onBeforeRequest: (config, request) => {
      if (!config.dataProps) return;
      if (request.type === 'loadView') {
        let {
          dataProps: { category, filter },
        } = config;
        let url = request.url;
        url = url.split('{category}').join(category);
        url = url.split('{filter}').join(filter);
        return {
          url,
        };
      }
      return;
    },
  },
  columns: [
    {
      id: 'Name',
      children: 'Name',
      serverSort: true,
      formatter: ({ data, config }) => {
        const params = {
          title: getTagValue(data.Tags, 'Title') || '',
          file: data.Name,
          jobId: getTagValue(data.Tags, 'JobId') || '',
          communicationId: getTagValue(data.Tags, 'CommunicationId') || '',
        };

        const encodedEnvironment = encodeURIComponent(useUser().Environment);
        const linkText = removeUnnecessaryExtension(params.title || data.Name);

        let location = '';
        if (params.communicationId) {
          if (
            data.Name !== 'communication.share' &&
            data.Name !== 'job.share'
          ) {
            location = getLinkLocation(
              `/${encodedEnvironment}/OnDemand`,
              `?${Utils.url.stringifySearchParams({
                communication: params.title,
                ContactId: useUser().Contact.SynchronizationId,
              })}`
            );
            return (
              <Formatter.Link
                text={linkText}
                to={location}
                component={RouterLink}
              />
            );
          }

          location = getLinkLocation(
            `/${encodedEnvironment}/File`,
            `?${Utils.url.stringifySearchParams(params)}`,
            `#documents/${encodeURIComponent(params.title)}`
          );

          return (
            <Formatter.Link
              text={linkText}
              to={location}
              component={RouterLink}
            />
          );
        }

        location = getLinkLocation(
          `/${encodedEnvironment}/File`,
          `?${Utils.url.stringifySearchParams(params)}`,
          `#workspace/${data.WorkspaceName}/${data.FilePath}`
        );

        return (
          <Formatter.Link
            text={linkText}
            to={location}
            component={RouterLink}
          />
        );
      },
      width: {
        xs: '30%',
      },
    },
    descriptionColumn,
    lastModifiedColumn,
  ],
};

export default {
  forms: FORMS,
  jobs: JOBS,
  analytics: ANALYTICS,
  documents: DOCUMENTS,
  custom: CUSTOM,
};
